/**
 * @author      Shivani Mehra
 * @date        Aug, 2024
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Card,
  Badge,
  Spinner,
  Row,
  Table,
  Dropdown,
} from "react-bootstrap";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import CampaignEdit from "./CampaignEdit";
import { ToastContainer, toast } from "react-toastify"; // npm i react-toastify --force
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import fileDownload from "js-file-download";
import helper from "../common/helper";
import * as constants from "../../constants/CONSTANT";
import Confirm from "../Confirm";
import { ShimmerTable } from "../common/ShimmerTable";
import { FaTrash, FaEdit, FaPlus } from "react-icons/fa";
import jwt_decode from "jwt-decode";

/** Distinct badge colors per status so Pending vs Completed vs In Progress are easy to scan */
function renderCampaignStatusBadge(row) {
  const raw = row.campaign_status || "Pending";
  const normalized = String(raw).toLowerCase().trim();
  const cls = "text-capitalize px-2 py-1 fw-semibold";
  if (normalized === "aborted" || normalized === "failed") {
    return (
      <Badge bg="danger" className={cls}>
        {raw}
      </Badge>
    );
  }
  if (normalized === "completed") {
    return (
      <Badge bg="success" className={cls}>
        {raw}
      </Badge>
    );
  }
  if (normalized === "pending") {
    return (
      <Badge bg="warning" text="dark" className={cls}>
        {raw}
      </Badge>
    );
  }
  if (normalized === "in progress") {
    return (
      <Badge bg="primary" className={cls}>
        {raw}
      </Badge>
    );
  }
  return (
    <Badge bg="secondary" className={cls}>
      {raw}
    </Badge>
  );
}

const Campaign = ({ selectedWhatsAppSetting }) => {
  const navigate = useNavigate();
  const [body, setBody] = useState([]);
  const [performanceCount, setPerformanceCount] = useState([]);
  const [isSpinner, setIsSpinner] = useState(false);
  const [modalShowHide, setModalShowHide] = useState(false);
  const [rowData, setRowData] = useState();
  const [showHideModel, setShowHideModel] = useState(false);
  const { campaign_name, sent, delivered, read, clicked, performance_score } =
    performanceCount;
  const [userInfo] = useState(jwt_decode(sessionStorage.getItem("token")));
  const bestPerformer = read !== "0";
  const canExport =
    userInfo?.userrole === "ADMIN" || userInfo?.can_export_leads === true;
  useEffect(() => {
    fetchData(selectedWhatsAppSetting);
    // fetchPerformance(selectedWhatsAppSetting);
  }, [selectedWhatsAppSetting]);

  const fetchData = async (selectedWhatsAppSetting) => {
    try {
      const result = await WhatsAppAPI.getCampaignData(selectedWhatsAppSetting);

      if (result.success) {
        const formattedResult = result.records.map((record) => {
          const formattedStartDate = moment(record.start_date).format(
            "M/D/YYYY, h:mm:ss A",
          );
          const groupNames = record.groups
            ? record.groups.map((group) => group.name).join(", ")
            : "";
          return {
            ...record,
            start_date: formattedStartDate,
            group_name: groupNames,
          };
        });

        setBody(formattedResult);
      } else {
        setBody([]);
      }
    } catch (error) {
      setBody([]);
      // console.error('Error fetching campaign data:', error);
    } finally {
      setIsSpinner(true);
    }
  };

  // Create table headers consisting of 4 columns.
  const labels = { beforeSelect: " " };
  const header = [
    {
      title: "Name",
      prop: "campaign_name",
      isFilterable: true,
      cell: (row) => (
        <Link to={`/campaign/track/${row.campaign_id}`} state={row}>
          {row.campaign_name}
        </Link>
      ),
    },
    {
      title: "Template Name",
      prop: "template_name",
      isFilterable: true,
      cell: (row) => row.template_name?.replace(/_/g, " ") || "-",
    },
    {
      title: "Schedule Date & Time",
      prop: "start_date",
      cell: (row) =>
        row.start_date
          ? moment(new Date(row.start_date)).format("DD-MM-YYYY h:mm:ss A")
          : "",
    },
    {
      title: "Total",
      prop: "total",
      isFilterable: true,
      cell: (row) => row.total ?? 0,
    },
    {
      title: "Delivered",
      prop: "delivered",
      isFilterable: true,
      cell: (row) => row.delivered ?? 0,
    },
    {
      title: "Read",
      prop: "read",
      isFilterable: true,
      cell: (row) => row.read ?? 0,
    },
    {
      title: "Clicked",
      prop: "clicked",
      isFilterable: true,
      cell: (row) => row.clicked ?? 0,
    },
    {
      title: "Read Rate",
      prop: "response_rate",
      isFilterable: true,
      cell: (row) => `${row.response_rate ?? 0}%`,
    },
    {
      title: "Status",
      prop: "campaign_status",
      isFilterable: true,
      cell: (row) => renderCampaignStatusBadge(row),
    },
    {
      title: "Actions",
      prop: "id",
      cell: (row) => {
        // HIDE actions when campaign name contains "Failure Reshoot"
        if (row.campaign_name?.toLowerCase().includes("failure reshoot")) {
          return <></>; // return empty UI
        }

        return (
          <Dropdown>
            <Dropdown.Toggle
              size="sm"
              variant="light"
              className="btn-action-icon"
              title="Actions"
            >
              <i className="fa-solid fa-bars"></i>
            </Dropdown.Toggle>

            <Dropdown.Menu className="shadow border-0 rounded">
              {row.campaign_status !== "Completed" &&
                row.campaign_status !== "Aborted" &&
                row.campaign_status !== "In Progress" && (
                  <Dropdown.Item onClick={() => editRecord({ row })}>
                    <i className="fa-solid fa-pen-to-square me-2 text-success"></i>{" "}
                    Edit
                  </Dropdown.Item>
                )}

              {(row.campaign_status === "Completed" ||
                row.campaign_status === "Pending") && (
                <Dropdown.Item onClick={() => cloneRecord({ row })}>
                  <i className="fa-solid fa-clone me-2 text-success"></i> Clone
                </Dropdown.Item>
              )}

              {row.campaign_status === "Pending" && (
                <Dropdown.Item onClick={() => deleteRecord({ row })}>
                  <i className="fa-regular fa-trash-can me-2 text-danger"></i>{" "}
                  Delete
                </Dropdown.Item>
              )}

              {row.file_title && canExport && (
                <Dropdown.Item onClick={() => downloadFile(row)}>
                  <i className="fa-solid fa-cloud-arrow-down me-2 text-primary"></i>{" "}
                  Download
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    },
  ];

  const createCampaignRecord = () => {
    navigate("/campaign/add");
  };

  const cloneRecord = (data) => {
    const startDate = moment(data?.row.start_date);
    if (!startDate.isValid()) {
      data.row.start_date = moment().format("YYYY-MM-DD HH:mm:ss");
    }
    navigate(`/campaign/${data?.row.campaign_id}`, {
      state: { ...data?.row, type: "clone" },
    });
  };

  const editRecord = (data) => {
    setModalShowHide(true);
    setRowData(data?.row);
  };

  const deleteRecord = (data) => {
    setShowHideModel(true);
    setRowData(data?.row);
  };

  const deleteCampaignRecord = async () => {
    if (rowData?.campaign_id) {
      const result = await WhatsAppAPI.deleteCampaignRecord(
        rowData.campaign_id,
      );
      if (result.success) {
        toast.success("Record deleted successfully.");
        fetchData(selectedWhatsAppSetting);
        setShowHideModel(false);
      } else {
        setShowHideModel(false);
        toast.error(result.error.message);
      }
    }
  };

  const onRefreshData = () => {
    toast.success("Record updated successfully.");
    fetchData(selectedWhatsAppSetting);
    setModalShowHide(false);
  };

  const downloadFile = async (row) => {
    try {
      if (!row?.file_title) {
        toast.error("File name required.");
        return;
      }

      const result = await WhatsAppAPI.downloadCampaignFile(row?.file_title);

      if (result) {
        fileDownload(result, row?.file_title);
        toast.success("File download successfully.");
      } else {
        toast.warning("File not found.");
      }
    } catch (error) {
      // console.error('Error downloading file:', error);
      if (error.message === "Unauthorized access. Please login.") {
        toast.error("Unauthorized access. Please login.");
      } else {
        toast.error("Failed to download file. Please try again.");
      }
    }
  };

  return (
    <>
      <Container className="mt-5 mb-5">
        <Row className="mx-5 mb-4">
          <Card className="p-4 header-bg shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">💡 Quick Guide</h5>
            <small>
              Start your WhatsApp campaign to send personalized messages,
              promote offers, and engage customers instantly—all in one place.
            </small>
          </Card>
        </Row>

        <Row className="mx-5 g-0">
          <Col lg={12} className="mb-3">
            <Row className="g-0">
              <Col lg={12}>
                <div className="bg-white shadow-sm border-0 rounded-3 p-4">
                  <h5 className="fw-semibold mb-1">📦 All Campaign</h5>

                  {isSpinner ? (
                    <DatatableWrapper
                      body={body}
                      headers={header}
                      paginationOptionsProps={{
                        initialState: {
                          rowsPerPage: 10,
                          options: [5, 10, 15, 20],
                        },
                      }}
                    >
                      <Row className="mb-3">
                        <Col
                          lg={3}
                          sm={10}
                          xs={10}
                          className="d-flex flex-col justify-content-end align-items-end"
                        >
                          <Filter className="p-2" />
                        </Col>
                        <Col
                          lg={4}
                          sm={2}
                          xs={2}
                          className="d-flex flex-col justify-content-start align-items-start"
                        >
                          <PaginationOptions labels={labels} />
                        </Col>
                        <Col
                          lg={5}
                          sm={12}
                          xs={12}
                          className="mt-2 d-flex flex-col justify-content-end align-items-end"
                        >
                          <Button
                            className="btn btn-sm"
                            variant="outline-secondary"
                            onClick={() => createCampaignRecord()}
                          >
                            <FaPlus /> Add New Campaign
                          </Button>
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table
                          striped
                          bordered
                          className="related-list-table"
                          responsive="sm"
                        >
                          <TableHeader />
                          <TableBody />
                        </Table>
                      </div>
                      <Pagination />
                    </DatatableWrapper>
                  ) : (
                    <ShimmerTable row={10} col={4} />
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
        {/* {isSpinner ?

                <Container className='mb-5'>
                    <Row className='mx-5 g-0'>
                        <Col lg={12} sm={12} xs={12} className="mb-3">
                            <Row className="g-0">
                                <Col lg={12} sm={12} xs={12} >
                                    <DatatableWrapper
                                        body={body}
                                        headers={header}
                                        paginationOptionsProps={{ initialState: { rowsPerPage: 10, options: [5, 10, 15, 20], } }}
                                    >
                                        <Row className="mb-2">
                                            <Col lg={4} sm={10} xs={10} className="d-flex flex-col justify-content-end align-items-end" >
                                                <Filter />
                                            </Col>
                                            <Col lg={4} sm={2} xs={2} className="d-flex flex-col justify-content-start align-items-start" >
                                                <PaginationOptions labels={labels} />
                                            </Col>

                                            <Col lg={4} sm={12} xs={12} className="mt-2 d-flex flex-col justify-content-end align-items-end"  >

                                                <Button className="btn btn-sm" variant="outline-secondary" onClick={() => createCampaignRecord()}>
                                                    Add New Campaign
                                                </Button>

                                            </Col>

                                        </Row>
                                        <Table striped className="data-table" responsive="sm">
                                            <TableHeader />
                                            <TableBody />
                                        </Table>
                                        <Pagination />
                                    </DatatableWrapper>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>

                :

                <div className="sk-cube-grid">
                    <div className="sk-cube sk-cube1"></div>
                    <div className="sk-cube sk-cube2"></div>
                    <div className="sk-cube sk-cube3"></div>
                    <div className="sk-cube sk-cube4"></div>
                    <div className="sk-cube sk-cube5"></div>
                    <div className="sk-cube sk-cube6"></div>
                    <div className="sk-cube sk-cube7"></div>
                    <div className="sk-cube sk-cube8"></div>
                    <div className="sk-cube sk-cube9"></div>
                </div>

            } */}
      </Container>

      {modalShowHide && (
        <CampaignEdit
          show={modalShowHide}
          onHide={() => {
            setModalShowHide(false);
          }}
          onRefreshData={onRefreshData}
          rowData={rowData}
          table="campaign"
          selectedWhatsAppSetting={selectedWhatsAppSetting}
        />
      )}
      {showHideModel && (
        <Confirm
          show={showHideModel}
          onHide={() => setShowHideModel(false)}
          deleteResponseRecord={deleteCampaignRecord}
          title="Confirm delete?"
          message="You are going to delete the record. Are you sure?"
          table="response_message"
        />
      )}

      <ToastContainer />
    </>
  );
};

export default Campaign;
